import { useCallback, useEffect, useMemo, useState } from "react";
import {
  RuleEditDialog,
  RuleFormValues,
} from "../../../../talentProfile/Employment/EmploymentAdditionalInfo/targetedShifts/RuleEditDialog";
import { isCompanyOnlyRule } from "../../../../talentProfile/Employment/EmploymentAdditionalInfo/targetedShifts/ruleHelpers";
import { RulesTable } from "../../../../talentProfile/Employment/EmploymentAdditionalInfo/targetedShifts/RulesTable";
import { useFieldArray, useFormContext } from "react-hook-form";
import { FormTitleGrid } from "../../../styles";
import Grid from "@mui/material/Grid/Grid";
import FormControlLabel from "@mui/material/FormControlLabel/FormControlLabel";
import Switch from "@mui/material/Switch/Switch";
import isEmpty from "../../../../../common/helpers/isEmpty";
import { useTranslation } from "react-i18next";
import { Typography } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import InfoTooltip from "../../../../../common/components/functional/InfoTooltip/InfoTooltip";
import { FlexVerticalAligned } from "../../../../../common/components/styled/styled";
import { isFulfilled, nanoid } from "@reduxjs/toolkit";
import FormGridRow from "../../../../../common/components/functional/formGridRow/FormGridRow";
import { useConfirmationDialog } from "../../../../../contexts/ConfirmationDialogContext";
import {
  fetchContractById,
  selectAllCustomerContracts,
} from "../../../../customer/customerContractsSlice";
import { CustomerContract } from "../../../../customer/customerAPI";
import { fetchEezyCompanies } from "../../../../invoices/invoiceDetailedSlice";
import { NameIdOption } from "../../../../../common/types/commonTypes";

type Props = {
  customerCompany: NameIdOption | null;
  customerOrg: NameIdOption | null;
  contractId?: string;
};

export const TargetedShifts = ({
  customerCompany,
  customerOrg,
  contractId,
}: Props) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation(["common", "order"]);
  const { confirm } = useConfirmationDialog();

  const { tes: localizedTes } = useAppSelector((state) => state.localized);
  const customerContract: CustomerContract | undefined = useAppSelector(
    (state) => selectAllCustomerContracts(state),
  ).find((contract) => contract.id === contractId);

  const [isRulesTableOpen, setIsRulesTableOpen] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState<RuleFormValues | null>(null);
  const [fetchedCompanyName, setFetchedCompanyName] = useState<string | null>(
    null,
  );

  const ruleInitialValues: Partial<RuleFormValues> | undefined = useMemo(
    () =>
      selectedRule
        ? {
            id: selectedRule?.id || nanoid(),
            customerCompany: selectedRule?.customerCompany || null,
            customerOrganization: selectedRule?.customerOrganization || null,
            workTitles: selectedRule?.workTitles ?? [],
            workLocations: selectedRule?.workLocations ?? [],
            isDefaultRule: selectedRule?.isDefaultRule ?? false,
          }
        : undefined,
    [selectedRule],
  );

  const { control, setValue, watch } = useFormContext();
  const { orderTemplate } = watch();

  const {
    fields: targetedShiftTargetingRules,
    append,
    update,
    replace,
  } = useFieldArray({
    control,
    name: "targetedShiftTargetingRules",
  });

  const handleAddClick = () => {
    setSelectedRule(null);
    setEditDialogOpen(true);
  };

  const handleEditClick = (rule: RuleFormValues) => {
    setSelectedRule(rule);
    setEditDialogOpen(true);
  };

  const handleRemoveClick = (id: string) => {
    const index = targetedShiftTargetingRules.findIndex((r) => r.id === id);
    if (index === -1) return;

    const remainingRules = targetedShiftTargetingRules.filter(
      (r) => r.id !== id,
    );
    replace(remainingRules);
  };

  const handleDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedRule(null);
  };

  const handleOneRuleSave = async (values: RuleFormValues): Promise<void> => {
    if (isCompanyOnlyRule(values)) {
      const confirmed = await confirm({
        title: t("talentProfile:targetedShifts.companyOnlyRuleTitle"),
        contentText: t("talentProfile:targetedShifts.companyOnlyRuleInfo"),
        confirmText: t("common:confirm"),
        cancelText: t("common:cancel"),
      });
      if (!confirmed) return;
    }

    const existingRuleIndex = targetedShiftTargetingRules.findIndex(
      (r) => r.id === values.id,
    );
    if (existingRuleIndex !== -1) {
      update(existingRuleIndex, values);
    } else {
      append(values);
    }
    handleDialogClose();
  };

  const fetchCompanyName = useCallback(
    (companyId: string) => {
      dispatch(fetchEezyCompanies({})).then((action) => {
        if (isFulfilled(action)) {
          const companyName = action.payload.find(
            (company) => company.eezyCompanyId === companyId,
          )?.name;
          setFetchedCompanyName(companyName || null);
        }
      });
    },
    [dispatch],
  );

  useEffect(() => {
    if (contractId) {
      if (!customerContract) {
        dispatch(fetchContractById({ contractId })).then((action) => {
          if (isFulfilled(action)) {
            const contract = action.payload;
            fetchCompanyName(contract.eezyCompanyId);
          }
        });
      } else {
        fetchCompanyName(customerContract.eezyCompanyId);
      }
    }
  }, [
    contractId,
    customerContract,
    targetedShiftTargetingRules,
    dispatch,
    fetchCompanyName,
  ]);

  return (
    <>
      <FormTitleGrid container item xs={4} />
      <Grid item xs={8}>
        <FormControlLabel
          onChange={(_, checked) => {
            if (!checked) {
              setValue("targetedShiftTargetingRules", [], {
                shouldDirty: true,
              });
            } else {
              setValue(
                "targetedShiftTargetingRules",
                [
                  {
                    customerCompany: customerCompany!!,
                    customerOrganization: customerOrg!!,
                    workLocations: [],
                    workTitles: [],
                    isDefaultRule: true,
                  },
                ],
                { shouldDirty: true },
              );
            }
          }}
          checked={!isEmpty(targetedShiftTargetingRules)}
          control={<Switch />}
          label={
            <FlexVerticalAligned>
              {t("order:setTargetedShifts")}
              <InfoTooltip
                title={t("order:targetingRulesInfo")}
                style={{ marginLeft: 3 }}
              />
            </FlexVerticalAligned>
          }
        />
      </Grid>
      {!isEmpty(targetedShiftTargetingRules) && (
        <>
          <FormGridRow
            label={t("common:eezyCompany")}
            labelSize={4}
            fieldSize={8}
          >
            <Typography>{fetchedCompanyName}</Typography>
          </FormGridRow>
          <FormGridRow label={t("common:tes")} labelSize={4} fieldSize={8}>
            <FlexVerticalAligned>
              <Typography>
                {orderTemplate?.tesId
                  ? localizedTes[orderTemplate?.tesId]
                  : "-"}
              </Typography>
              <InfoTooltip
                title={t("order:targetingRulesTesInfo")}
                style={{ marginLeft: 3 }}
              />
            </FlexVerticalAligned>
          </FormGridRow>
          <FormGridRow
            label={t("common:customerOrganization")}
            labelSize={4}
            fieldSize={8}
          >
            {customerOrg?.name}
          </FormGridRow>
          <FormTitleGrid container item xs={2} />
          <Grid item xs={10}>
            <RulesTable
              rules={targetedShiftTargetingRules}
              addRow={handleAddClick}
              editRow={handleEditClick}
              deleteRow={handleRemoveClick}
              isCollapsed={!isRulesTableOpen}
              onToggleCollapse={() => setIsRulesTableOpen((prev) => !prev)}
            />
          </Grid>
        </>
      )}
      <RuleEditDialog
        open={editDialogOpen}
        onClose={handleDialogClose}
        onSave={handleOneRuleSave}
        initialValues={ruleInitialValues}
        contractId={contractId}
        customerCompany={customerCompany}
      />
    </>
  );
};
